import { useEffect, useState } from "react";
import {
  RotateCcw,   CheckCircle,  XCircle,
    Search,  Filter,  Eye,  Package,  AlertTriangle,  ChevronDown,
  ChevronUp,  Camera,  FileText,  Truck,  User,  Calendar,  IndianRupee,  X,  Plus, 
   PackageX ,MapPin,  Phone,  Mail,  Ticket
  } from "lucide-react";
import { Card } from "../../Components/Card";
import { Button } from "../../Components/Button";
import { Badge } from "../../Components/Badge";
import { Input } from "../../Components/Input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../Components/Tabs";
import { Toaster as AppToaster } from "../../Components/Sonner";
import Pagination from "../../Components/Pagination";
import { toast } from "sonner";
import { decryptData } from '../../../utils/storageCrypto';
import {CreateRequestModal} from './RequestModal';


const reasonLabels = {
  DEFECTIVE_ITEM: "Defective Product",
  WRONG_ITEMS_DELIVERED: "Wrong Item(s) Delivered",
  NOT_AS_DESCRIBED: "Product Not as Described",
  GOODS_DAMAGED: "Damaged Item(s) Received",
  CHANGED_MIND: "Changed Mind",
  NO_LONGER_NEEDED: "No Longer Needed",
  DUPLICATE_PURCHASE: "Duplicate Purchase",
  DELIVERED_LATE: "Delivered Late",
  EXPIRED_PRODUCT: "Expired Product",
  QUALITY_ISSUE: "Quality Issue",
  OTHER: "Other",
};

const statusConfig = {
  pending_review: { label: "Pending Review", color: "bg-yellow-100 text-yellow-800", variant: "outline" },
  approved: { label: "Approved", color: "bg-green-100 text-green-800", variant: "default" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800", variant: "destructive" },
  inspection: { label: "Under Inspection", color: "bg-blue-100 text-blue-800", variant: "secondary" },
  completed: { label: "Completed", color: "bg-gray-100 text-gray-800", variant: "secondary" },
  refund_issued: { label: "Refund Issued", color: "bg-purple-100 text-purple-800", variant: "default" },
};

const replacementStatusConfig = {
  requested: { label: "Requested", color: "bg-yellow-100 text-yellow-800" },
  approved: { label: "Approved", color: "bg-green-100 text-green-800" },
  processing: { label: "Processing", color: "bg-blue-100 text-blue-800" },
  shipped: { label: "Shipped", color: "bg-indigo-100 text-indigo-800" },
  delivered: { label: "Delivered", color: "bg-gray-100 text-gray-800" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800" },
};

const normalizeReturn = (item) => {
  //console.log("item:",item);
  const statusMap = {
    RETURN_REQUESTED: "pending_review",
    RETURN_APPROVED: "approved",
    RETURN_REJECTED: "rejected",
    RETURN_RECEIVED_AT_HUB: "inspection",
    INSPECTION_PASSED: "approved",
    INSPECTION_FAILED: "rejected",
    DISPUTE_HOLD: "inspection",
    PICKUP_SCHEDULED: "approved",
  };

  const normalizedStatus = statusMap[item.status] || item.status || "pending_review";
  const requestedQty = item?.items?.reduce((sum, current) => sum + Number(current.requested_qty || 0), 0) || 0;
  const totalValue = Number(item.total_amount || item.refund_amount || 0);

  return {
    id: item._id || item.id || "",
    returnNumber:item.return_number,
    orderId: item.order_id?.quick_buy_order_number || item.order_id?.salesorder_number || item.order_id || "",
    customerId: item.customer_id?._id || item.customer_id || "",
    customerName: item.customer_id?.company_name || item.customer_id?.customer_name || "Customer",
    customerEmail: item.customer_email || "",
    product: item.item_name || item.items?.[0]?.item_name || "Return item",
    sku: item.sku || item.items?.[0]?.sku || "",
    quantity: requestedQty || item.items?.[0]?.requested_qty || 1,
    unitPrice: item.unit_price || 0,
    totalValue: totalValue || requestedQty * (item.unit_price || 0),
    reason: reasonLabels[item.reason_code] || reasonLabels.OTHER,
    reasonCode: item.reason_code || "OTHER",
    reasonDetail:
      reasonLabels[item.reason_code] ||
      item.rejection_reason ||
      "No details provided",
    status: normalizedStatus,
    requestDate: item.requested_at ? new Date(item.requested_at).toISOString().split("T")[0] : "",
    lastUpdated: item.updated_at ? new Date(item.updated_at).toISOString().split("T")[0] : item.requestDate,
    hasPhotos: Boolean(item.media?.length),
    inspectionNotes: item.inspection?.notes || item.notes || "",
    refundAmount: item.refund_amount || null,
    priority: item.priority || "medium",
    raw: item,
  };
};

const normalizeReplacement = (item) => {
  const statusMap = {
    REPLACEMENT_REQUESTED: "requested",
    REPLACEMENT_APPROVED: "approved",
    REPLACEMENT_REJECTED: "rejected",
    REPLACEMENT_PICKUP_SCHEDULED: "processing",
    REPLACEMENT_RECEIVED_AT_HUB: "processing",
    INSPECTION_PASSED: "processing",
    REPLACEMENT_DISPATCHED: "shipped",
    DELIVERED: "delivered",
  };

  const normalizedStatus = statusMap[item.status] || item.status || "requested";

  return {
    id: item._id || item.id || "",
    replacementNumber: item.replacement_number,
    returnId: item.return_number || item.return_id || "",
    orderId: item.order_id?.quick_buy_order_number || item.order_id?.salesorder_number || item.order_id || "",
    customerId: item.customer_id?._id || item.customer_id || "",
    customerName: item.customer_id?.customer_name || item.customer_id?.company_name || "Customer",
    product: item.product_name || item.items?.item_name || "Replacement item",
    sku: item.sku || item.items?.sku || "",
    quantity: Number(item.items?.requested_qty || 1),
    status: normalizedStatus,
    requestDate: item.requested_at ? new Date(item.requested_at).toISOString().split("T")[0] : "",
    lastUpdated: item.updated_at ? new Date(item.updated_at).toISOString().split("T")[0] : item.requestDate,
    notes: item.notes || item.reason_detail || "",
    priority: item.priority || "medium",
    trackingNumber: item.awb_number || item.tracking_number || "",
    estimatedDelivery: item.estimated_delivery || "",
    raw: item,
  };
};

export function ReturnDetailPanel({  item,  onClose,  onStatusChange,  setRejectModal,}) {
  const [notes, setNotes] = useState(item.inspectionNotes || "");
  const [refundAmount, setRefundAmount] = useState(
    item.refundAmount?.toString() || ""
  );

  if (!item) return null;
  console.log(item);

  /* -------------------------------------------------------------------------- */
  /*                         DATA NORMALIZATION & EXTRACTION                    */
  /* -------------------------------------------------------------------------- */
  const rawCustomer = item.raw?.customer_id || {};
  const rawOrder = item.raw?.order_id || {};
  const rawItems = item.raw?.items || [];
  const primaryZone = rawCustomer.zones?.find((z) => z.is_primary) || rawCustomer.zones?.[0];

  // Address resolution
  const addressParts = [
    primaryZone?.address1?.trim() || rawCustomer.address?.trim(),
    primaryZone?.city?.trim(),
    primaryZone?.state?.trim(),
    primaryZone?.pincode?.trim() || rawCustomer.pincode?.trim(),
  ].filter(Boolean);

  const formattedAddress = addressParts.length > 0 ? addressParts.join(", ") : null;

  // Media resolution
  const mediaList = Array.isArray(item.raw?.media)
    ? item.raw.media.filter((m) => m?.url)
    : [];

  const ticketNumber = item.raw?.ticket_number;
  const returnMode = item.raw?.return_mode;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-3 sm:p-5"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative flex flex-col w-full max-w-3xl h-[92vh] max-h-[850px] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
        
        {/* -------------------- HEADER (Fixed) -------------------- */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 bg-white px-6 py-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h2 className="text-lg font-bold text-gray-900">
                {item.returnNumber || "Return Request"}
              </h2>

              {item.status && statusConfig?.[item.status] && (
                <span
                  className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold ${statusConfig[item.status].color}`}
                >
                  {statusConfig[item.status].label}
                </span>
              )}
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
              {item.orderId && <span>Order #{item.orderId}</span>}
              {ticketNumber && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="inline-flex items-center gap-1 font-medium text-gray-700">
                    <Ticket className="h-3.5 w-3.5 text-gray-400" />
                    Ticket: {ticketNumber}
                  </span>
                </>
              )}
              {returnMode && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="inline-flex items-center gap-1 font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded text-[11px]">
                    <Truck className="h-3 w-3" />
                    {returnMode.replace(/_/g, " ")}
                  </span>
                </>
              )}
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 text-gray-400 hover:text-gray-600 rounded-lg"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* -------------------- BODY CONTENT (Scrollable) -------------------- */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5">
          
          {/* CUSTOMER & PRODUCT CARDS */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            
            {/* 1. CUSTOMER CARD */}
            <div className="rounded-xl border border-gray-200/80 bg-slate-50/50 p-4 space-y-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                <User className="h-4 w-4 text-gray-400" />
                Customer Details
              </div>

              <div className="space-y-1.5">
                <p className="font-semibold text-sm text-gray-900">
                  {rawCustomer.customer_name || item.customerName || "—"}
                </p>

                {rawCustomer.mobile_number && (
                  <p className="flex items-center gap-2 text-xs text-gray-600">
                    <Phone className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span>+91 {rawCustomer.mobile_number}</span>
                  </p>
                )}

                {rawCustomer.email && (
                  <p className="flex items-center gap-2 text-xs text-gray-600">
                    <Mail className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span className="truncate">{rawCustomer.email}</span>
                  </p>
                )}

                {formattedAddress && (
                  <div className="flex items-start gap-2 pt-1 text-xs text-gray-600">
                    <MapPin className="h-3.5 w-3.5 text-gray-400 shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{formattedAddress}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 2. PRODUCT & ITEMS CARD */}
            <div className="rounded-xl border border-gray-200/80 bg-slate-50/50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  <Package className="h-4 w-4 text-gray-400" />
                  Item Details
                </div>
                <span className="text-xs text-gray-400">
                  {rawItems.length} {rawItems.length === 1 ? "item" : "items"}
                </span>
              </div>

              <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto pr-1">
                {rawItems.length > 0 ? (
                  rawItems.map((prod, idx) => (
                    <div key={prod._id || idx} className="py-2 first:pt-0 last:pb-0">
                      <p className="font-medium text-sm text-gray-900 leading-snug">
                        {prod.item_name || item.product}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>
                          Qty: <strong className="text-gray-800">{prod.requested_qty || item.quantity}</strong>
                        </span>
                        {prod.sku && <span>SKU: {prod.sku}</span>}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-2">
                    <p className="font-medium text-sm text-gray-900">
                      {typeof item.product === "string" ? item.product : "Item"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Qty: <strong className="text-gray-800">{item.quantity}</strong>
                    </p>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* RETURN REASON & MEDIA */}
          <div className="rounded-xl border border-gray-200/80 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Reason for Return
              </div>
              {(reasonLabels?.[item.reasonCode] || item.reasonCode) && (
                <span className="text-xs font-medium bg-amber-50 text-amber-800 px-2.5 py-0.5 rounded-full border border-amber-200">
                  {reasonLabels?.[item.reasonCode] || item.reasonCode.replace(/_/g, " ")}
                </span>
              )}
            </div>

            {item.reasonDetail && item.reasonDetail !== "No details provided" && (
              <p className="text-xs text-gray-700 leading-relaxed bg-gray-50 p-3 rounded-lg border border-gray-100">
                {item.reasonDetail}
              </p>
            )}

            {mediaList.length > 0 && (
              <div className="pt-2">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2">
                  <Camera className="h-3.5 w-3.5 text-gray-400" />
                  Proof Photos ({mediaList.length})
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {mediaList.map((img, i) => (
                    <a
                      key={img._id || i}
                      href={`${process.env.REACT_APP_FORE_BRICKBUCKET}${img.url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="group relative h-16 w-16 overflow-hidden rounded-lg border border-gray-200 bg-gray-100"
                    >
                      <img
                        src={`${process.env.REACT_APP_FORE_BRICKBUCKET}${img.url}`}
                        alt="Proof"
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* TIMELINE */}
          <div className="rounded-xl border border-gray-200/80 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
              <Calendar className="h-4 w-4 text-gray-400" />
              Timeline & Logs
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
              <div>
                <span className="text-gray-400 block font-medium">Requested On</span>
                <p className="font-semibold text-gray-800 mt-0.5">
                  {item.requestDate || "—"}
                </p>
              </div>

              {item.lastUpdated && (
                <div>
                  <span className="text-gray-400 block font-medium">Last Updated</span>
                  <p className="font-semibold text-gray-800 mt-0.5">
                    {item.lastUpdated}
                  </p>
                </div>
              )}

              {item.trackingNumber && (
                <div>
                  <span className="text-gray-400 block font-medium">Tracking Number</span>
                  <p className="font-semibold font-mono text-gray-800 mt-0.5">
                    {item.trackingNumber}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* INSPECTION / ADMIN NOTES */}
          <div className="rounded-xl border border-gray-200/80 p-4 space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              <FileText className="h-4 w-4 text-gray-400" />
              Inspection / Admin Notes
            </div>
            <textarea
              className="w-full rounded-lg border border-gray-300 p-2.5 text-xs text-gray-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 resize-none"
              rows={3}
              placeholder="Add inspection notes or internal comments..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* REFUND AMOUNT (Only if required / editing) */}
          {(item.status === "pending_review" || item.status === "inspection") && item.refundAmount && (
            <div className="rounded-xl border border-gray-200/80 p-4 space-y-2">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                <IndianRupee className="h-4 w-4 text-emerald-600" />
                Refund Amount
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-500">₹</span>
                <Input
                  type="number"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  className="w-40 h-8 text-xs font-semibold"
                />
              </div>
            </div>
          )}

        </div>

        {/* -------------------- ACTION FOOTER (Sticky) -------------------- */}
        <div className="shrink-0 border-t border-gray-100 bg-gray-50/80 px-6 py-3.5 flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="text-xs font-medium text-gray-600 hover:bg-white"
          >
            Close
          </Button>

          <div className="flex items-center gap-2">
            {item.status === "pending_review" && (
              <>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => {
                    setRejectModal({
                      open: true,
                      id: item.id,
                      type: "return",
                    });
                    onClose();
                  }}
                  className="text-xs h-8 font-medium bg-red-600 hover:bg-red-700"
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" />
                  Reject
                </Button>

                <Button
                  size="sm"
                  onClick={() => {
                    onStatusChange(item.id, "approved", notes);
                    onClose();
                  }}
                  className="text-xs h-8 font-medium bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <CheckCircle className="w-3.5 h-3.5 mr-1" />
                  Approve
                </Button>
              </>
            )}

            {item.status === "inspection" && (
              <>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => {
                    onStatusChange(item.id, "rejected", notes);
                    onClose();
                  }}
                  className="text-xs h-8 font-medium bg-red-600 hover:bg-red-700"
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" />
                  Reject
                </Button>

                <Button
                  size="sm"
                  onClick={() => {
                    onStatusChange(item.id, "approved", notes);
                    onClose();
                  }}
                  className="text-xs h-8 font-medium bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <CheckCircle className="w-3.5 h-3.5 mr-1" />
                  Approve Inspection
                </Button>
              </>
            )}

          {/*   {item.status === "approved" && (
              <Button
                size="sm"
                onClick={() => {
                  onStatusChange(item.id, "completed", notes);
                  onClose();
                }}
                className="text-xs h-8 font-medium bg-blue-600 hover:bg-blue-700 text-white"
              >
                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                Mark Completed
              </Button>
            )} */}

            {(item.status === "completed" ||
              item.status === "refund_issued" ||
              item.status === "rejected") && (
              <span className="text-xs text-gray-500 font-medium">
                Resolved
              </span>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

function ReplacementDetailPanel({ item, onClose, onStatusChange, setRejectModal  }) {
  const [notes, setNotes] = useState(item.notes || "");
  const [tracking, setTracking] = useState(item.trackingNumber || "");

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 py-6">
      <div className="my-auto flex w-full max-w-[680px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl" style={{ maxHeight: "calc(100vh - 3rem)" }}>
        <div className="flex flex-shrink-0 items-center justify-between border-b p-5">
          <div>
            <div className="flex items-center gap-3">
              <h3>{item.replacementNumber}</h3>
              <span
                className={`text-xs font-medium px-2.5 py-1 rounded-full ${replacementStatusConfig[item.status].color}`}
              >
                {replacementStatusConfig[item.status].label}
              </span>
            </div>
            <p className="text-muted-foreground mt-1">
              Return {item.returnId} · {item.customerName}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="space-y-4 p-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Card className="h-full p-4 space-y-1">
                <p className="text-xs text-muted-foreground">Customer</p>
                <p className="font-medium">{item.customerName}</p>
                <p className="text-xs text-muted-foreground">ID: {item.customerId}</p>
              </Card>
              <Card className="h-full p-4 space-y-1">
                <p className="text-xs text-muted-foreground">Replacement Product</p>
                <p className="font-medium">{item.product}</p>
                <p className="text-xs text-muted-foreground">
                  SKU: {item.sku} · Qty: {item.quantity}
                </p>
              </Card>
            </div>

            <Card className="p-4">
              <p className="text-xs text-muted-foreground mb-2">Notes</p>
              <textarea
                className="w-full border rounded-lg p-3 text-sm resize-none bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes..."
              />
            </Card>

            {(item.status === "approved" || item.status === "processing" || item.status === "shipped") && (
              <Card className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-3">
                  <Truck className="w-4 h-4" />
                  <span className="text-sm font-medium">Shipping Information</span>
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground">Tracking Number</label>
                  <Input
                    value={tracking}
                    onChange={(e) => setTracking(e.target.value)}
                    placeholder="Enter tracking number..."
                  />
                  {item.estimatedDelivery && (
                    <p className="text-xs text-muted-foreground">
                      Estimated delivery: {item.estimatedDelivery}
                    </p>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 border-t bg-muted/30 p-5">
          {item.status === "requested" && (
            <div className="flex flex-wrap gap-3">
              <Button
                className="flex-1 min-w-[180px] bg-green-600 hover:bg-green-700"
                onClick={() => { onStatusChange(item.id, "approved", notes); onClose(); }}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve Replacement
              </Button>
              <Button
                variant="destructive"
                className="flex-1 bg-red-600 hover:bg-red-700"
                //onClick={() => { onStatusChange(item.id, "rejected", notes); onClose(); }}
                onClick={() => {
                    setRejectModal({
                        open: true,
                        id: item.id,
                        type: "replacement",
                    });
                    onClose();
                }}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
            </div>
          )}

          {item.status === "approved" && (
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={() => { onStatusChange(item.id, "processing", notes); onClose(); }}
            >
              <Package className="w-4 h-4 mr-2" />
              Start Processing
            </Button>
          )}

          {item.status === "processing" && (
            <Button
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              onClick={() => { onStatusChange(item.id, "shipped", notes); onClose(); }}
            >
              <Truck className="w-4 h-4 mr-2" />
              Mark as Shipped
            </Button>
          )}

          {item.status === "shipped" && (
            <Button
              className="w-full bg-green-600 hover:bg-green-700"
              onClick={() => { onStatusChange(item.id, "delivered", notes); onClose(); }}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirm Delivered
            </Button>
          )}

          {(item.status === "delivered" || item.status === "rejected") && (
            <div className="text-center text-sm text-muted-foreground">
              Replacement is resolved. No further actions required.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ReturnRow({ item, onSelect, onQuickAction, setRejectModal }) {
  const [expanded, setExpanded] = useState(false);
  const [showPhotos, setShowPhotos] = useState(false);

  if (!item) return null;

  const isPending =
    item.status === "pending_review" || item.status === "inspection";

  const mediaList = Array.isArray(item.raw?.media)
    ? item.raw.media.filter((m) => m?.url)
    : [];
  const hasPhotos = mediaList.length > 0;

  // Priority indicator styles
  const priorityColor =
    item.priority === "high"
      ? "bg-red-500 ring-red-100"
      : item.priority === "medium"
      ? "bg-amber-500 ring-amber-100"
      : "bg-slate-400 ring-slate-100";

  return (
    <div className="rounded-xl border border-gray-200/90 bg-white transition-all duration-150 hover:border-gray-300 hover:shadow-xs">
      {/* -------------------- MAIN ROW BAR -------------------- */}
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4">
        {/* Status indicator dot */}
      {/*   <div className="hidden sm:block">
          <div
            className={`h-2.5 w-2.5 shrink-0 rounded-full ring-4 ${priorityColor}`}
            title={`Priority: ${item.priority || "Normal"}`}
          />
        </div> */}

        {/* Content Info */}
        <div className="min-w-0 flex-1">
          {/* Header row: Return Number, Order ID, and Status Badge */}
          <div className="flex flex-wrap items-center gap-2">
            {item.returnNumber && (
              <span className="text-sm font-semibold tracking-tight text-gray-900">
                {item.returnNumber}
              </span>
            )}

            {item.orderId && (
              <>
                <span className="text-gray-300">·</span>
                <span className="text-xs font-medium text-gray-500">
                  Order {item.orderId}
                </span>
              </>
            )}

            {item.status && statusConfig?.[item.status] && (
              <span
                className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${statusConfig[item.status].color}`}
              >
                {statusConfig[item.status].label}
              </span>
            )}
          </div>

          {/* Product Name */}
          {item.product && (
            <p className="mt-1 truncate text-sm font-medium text-gray-800">
              {item.product}
            </p>
          )}

          {/* Secondary Details: Date on the left, then Customer & Reason */}
          {(item.requestDate || item.customerName || (item.reason && reasonLabels?.[item.reason])) && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
              {/* Date mapped at the left */}
              {item.requestDate && (
                <span className="font-medium text-gray-600">
                  {item.requestDate}
                </span>
              )}

              {item.requestDate && item.customerName && (
                <span className="text-gray-300">·</span>
              )}

              {item.customerName && (
                <span>{item.customerName}</span>
              )}

              {item.customerName && item.reason && reasonLabels?.[item.reason] && (
                <span className="text-gray-300">·</span>
              )}

              {item.reason && reasonLabels?.[item.reason] && (
                <span className="text-gray-600">
                  {reasonLabels[item.reason]}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2 pt-2 sm:pt-0 shrink-0">
          {isPending && (
            <>
              <Button
                size="sm"
                className="h-8 gap-1.5 bg-emerald-600 px-3 text-xs font-medium text-white hover:bg-emerald-700"
                onClick={() => onQuickAction(item.id, "approved")}
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Approve
              </Button>

              <Button
                size="sm"
                variant="destructive"
                className="h-8 gap-1.5 bg-red-600 px-3 text-xs font-medium text-white hover:bg-red-700"
                onClick={() =>
                  setRejectModal({
                    open: true,
                    id: item.id,
                    type: "return",
                  })
                }
              >
                <XCircle className="h-3.5 w-3.5" />
                Reject
              </Button>
            </>
          )}

          <Button
            size="sm"
            variant="outline"
            className="h-8 border-gray-300 px-3 text-xs font-medium text-gray-700 hover:bg-gray-50"
            onClick={() => onSelect(item)}
          >
            Details
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? "Collapse details" : "Expand details"}
          >
            {expanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* -------------------- EXPANDED SECTION -------------------- */}
      {expanded && (
        <div className="space-y-3 border-t border-gray-100 bg-slate-50/70 p-4 rounded-b-xl">
          {/* Reason Detail */}
          {item.reasonDetail && (
            <div className="flex items-start gap-2 text-xs text-gray-700 leading-relaxed">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500 mt-0.5" />
              <div>
                <span className="font-semibold text-gray-800">Reason Details: </span>
                {item.reasonDetail}
              </div>
            </div>
          )}

          {/* Inspection / Admin Notes */}
          {item.inspectionNotes && (
            <div className="flex items-start gap-2 text-xs text-gray-700 leading-relaxed">
              <FileText className="h-4 w-4 shrink-0 text-blue-500 mt-0.5" />
              <div>
                <span className="font-semibold text-gray-800">Inspection Notes: </span>
                {item.inspectionNotes}
              </div>
            </div>
          )}

          {/* Media & Refund Footer */}
          {(hasPhotos || item.refundAmount) && (
            <div className="flex flex-wrap items-center gap-4 pt-1">
              {hasPhotos && (
                <button
                  type="button"
                  onClick={() => setShowPhotos(true)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800"
                >
                  <Camera className="h-3.5 w-3.5" />
                  Photos attached ({mediaList.length})
                </button>
              )}

              {item.refundAmount && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                  <IndianRupee className="h-3 w-3" />
                  Refund: ₹{Number(item.refundAmount).toLocaleString("en-IN")}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* -------------------- PHOTO PREVIEW MODAL -------------------- */}
      {showPhotos && hasPhotos && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
          <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  Customer Return Photos
                </h3>
                {item.returnNumber && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {item.returnNumber}
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowPhotos(false)}
                className="h-8 w-8 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid max-h-[70vh] grid-cols-2 gap-4 overflow-y-auto p-6 sm:grid-cols-3">
              {mediaList.map((image, index) => (
                <div
                  key={image._id || index}
                  className="group relative aspect-square overflow-hidden rounded-xl border border-gray-200 bg-gray-50"
                >
                  <img
                    src={`${process.env.REACT_APP_FORE_BRICKBUCKET}${image.url}`}
                    alt={`Proof photo ${index + 1}`}
                    className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ReplacementRow({ item, onSelect, onQuickAction, setRejectModal  }) {
  return (
    <div className="flex items-center gap-4 p-4 border rounded-lg hover:bg-accent/30 transition-colors">
      <div
        className={`w-2 h-2 rounded-full flex-shrink-0 ${
          item.priority === "high"
            ? "bg-red-500"
            : item.priority === "medium"
            ? "bg-yellow-500"
            : "bg-gray-400"
        }`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <span className="font-mono text-sm font-semibold">{item.replacementNumber}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-xs text-muted-foreground">{item.returnId}</span>
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${replacementStatusConfig[item.status].color}`}
          >
            {replacementStatusConfig[item.status].label}
          </span>
        </div>
        <p className="text-sm font-medium mt-0.5 truncate">{item.product}</p>
        <p className="text-xs text-muted-foreground">
          {item.customerName} · Qty: {item.quantity}
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        {item.trackingNumber ? (
          <div className="flex items-center gap-1 text-xs text-indigo-600">
            <Truck className="w-3 h-3" />
            <span className="font-mono">{item.trackingNumber}</span>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">{item.requestDate}</p>
        )}
        {item.estimatedDelivery && (
          <p className="text-xs text-muted-foreground mt-0.5">
            ETA: {item.estimatedDelivery}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {item.status === "requested" && (
          <>
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white h-8 px-3"
              onClick={() => onQuickAction(item.id, "approved")}
            >
              <CheckCircle className="w-3.5 h-3.5 mr-1" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="bg-red-600 hover:bg-red-700 text-white h-8 px-3"
              //onClick={() => onQuickAction(item.id, "rejected")}
              onClick={() =>
                setRejectModal({
                  open: true,
                  id: item.id,
                  type: "replacement",
                })
              }
            >
              <XCircle className="w-3.5 h-3.5 mr-1" />
              Reject
            </Button>
          </>
        )}
        <Button size="sm" variant="outline" onClick={() => onSelect(item)}>
          Details
        </Button>
      </div>
    </div>
  );
}


/* function CreateRequestModal({ type, onClose }) {
  const [form, setForm] = useState({
    orderId: "",
    customer: "",
    product: "",
    quantity: 1,
    reason: "",
    notes: "",
  });

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = () => {
    console.log(type, form);

    toast.success(
      `${type === "return" ? "Return" : "Replacement"} created successfully`
    );

    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 space-y-5">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">
            Create {type === "return" ? "Return" : "Replacement"}
          </h2>

          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <Input
          placeholder="Order ID"
          value={form.orderId}
          onChange={(e) => handleChange("orderId", e.target.value)}
        />

        <Input
          placeholder="Customer"
          value={form.customer}
          onChange={(e) => handleChange("customer", e.target.value)}
        />

        <Input
          placeholder="Product"
          value={form.product}
          onChange={(e) => handleChange("product", e.target.value)}
        />

        <Input
          type="number"
          placeholder="Quantity"
          value={form.quantity}
          onChange={(e) => handleChange("quantity", e.target.value)}
        />

        <textarea
          className="w-full border rounded-md p-3"
          rows={3}
          placeholder="Reason / Notes"
          value={form.reason}
          onChange={(e) => handleChange("reason", e.target.value)}
        />

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>

          <Button onClick={handleSubmit}>
            Create {type === "return" ? "Return" : "Replacement"}
          </Button>
        </div>
      </div>
    </div>
  );
} */

export function ReturnsManager() {
  const [returns, setReturns] = useState([]);
  const [replacements, setReplacements] = useState([]);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [selectedReplacement, setSelectedReplacement] = useState(null);
  const [returnsSearch, setReturnsSearch] = useState("");
  const [returnsStatusFilter, setReturnsStatusFilter] = useState([]);
  const [replacementsSearch, setReplacementsSearch] = useState("");
  const [replacementsStatusFilter, setReplacementsStatusFilter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState("");
  const [returnsPage, setReturnsPage] = useState(1);
  const [replacementsPage, setReplacementsPage] = useState(1);
  const [showReturnsStatusDropdown, setShowReturnsStatusDropdown] = useState(false);
  const [showReplacementsStatusDropdown, setShowReplacementsStatusDropdown] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState("returns");
  const [createType, setCreateType] = useState("return"); // return | replacement
  const backendBaseUrl = process.env.REACT_APP_FORE_BRICKBUCKET;
  const pageSize = 5;
  const returnStatusOptions = Object.keys(statusConfig);
  const replacementStatusOptions = Object.keys(replacementStatusConfig);
  const encryptedToken = sessionStorage.getItem('token');
  const token = decryptData(encryptedToken);
  const [rejectModal, setRejectModal] = useState({
    open: false,
    id: "",
    type: "", // "return" | "replacement"
  });

  const [rejectionReason, setRejectionReason] = useState("");
  
  const fetchRequests = async () => {
    try {
      setLoading(true);
      setApiError("");
      const response = await fetch(`${backendBaseUrl}returns/get-all-return-requests`, {
        headers: {
          "x-token": token,
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();
      console.log("result:",result);
      const payload = result?.data || {};
      setReturns((payload || []).map(normalizeReturn));
    } catch (error) {
      console.error("Failed to load returns data", error);
      //setApiError("Unable to load live returns data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

 const handleReturnStatusChange = async (id, status, notes) => {
    try {
      const endpoint = status === "rejected" ? "returns/reject-return" : "returns/approve-return";

      // Payload strictly according to specification:
      // 1. Approve: { id, pickup_charge_confirmed: true }
      // 2. Reject:  { id, rejection_reason }
      const requestBody =
        status === "rejected"
          ? {
              id: id,
              rejection_reason: notes || "No reason provided",
            }
          : {
              id: id,
              pickup_charge_confirmed: true,
            };

      const response = await fetch(`${backendBaseUrl}${endpoint}`, {
        method: "POST",
        headers: {
          "x-token": token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.success === false) {
        throw new Error(
          result.message ||
            result.error ||
            `Failed to ${status === "rejected" ? "reject" : "approve"} return`
        );
      }

      // Optimistic / local state update
      setReturns((prev) =>
        prev.map((r) =>
          r.id === id
            ? {
                ...r,
                status: status === "rejected" ? "rejected" : "approved",
                inspectionNotes: notes || r.inspectionNotes,
                lastUpdated: new Date().toISOString().split("T")[0],
              }
            : r
        )
      );

      // Re-fetch latest records from backend to sync state
      await fetchRequests();

      toast.success(
        result.message ||
          `Return ${status === "rejected" ? "rejected" : "approved"} successfully`
      );
    } catch (error) {
      console.error(`Failed to update return ${id}:`, error);
      toast.error(error.message || "Failed to update return status");
    }
  };

  const handleReplacementStatusChange = async (id, status, notes) => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const endpoint = status === "rejected" ? "reject-replacement" : "approve-replacement";
      const response = await fetch(`${backendBaseUrl}returns/${endpoint}`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: id.replace(/^RP-/, ""), rejection_reason: notes || "No reason provided" }),
      });

      if (!response.ok) {
        throw new Error("Unable to update replacement status");
      }

      setReplacements((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, status, notes: notes || r.notes, lastUpdated: new Date().toISOString().split("T")[0] }
            : r
        )
      );
      toast.success(`Replacement ${id} status updated to ${replacementStatusConfig[status].label}`);
    } catch (error) {
      console.error("Failed to update replacement", error);
      toast.success(`Replacement ${id} updated locally`);
    }
  };

  const toggleReturnStatus = (status) => {
    setReturnsStatusFilter((prev) => {
      const next = prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status];
      return next;
    });
    setReturnsPage(1);
  };

  const toggleReplacementStatus = (status) => {
    setReplacementsStatusFilter((prev) => {
      const next = prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status];
      return next;
    });
    setReplacementsPage(1);
  };

  const toggleReturnSelectAll = () => {
    if (returnsStatusFilter.length === returnStatusOptions.length) {
      setReturnsStatusFilter([]);
    } else {
      setReturnsStatusFilter(returnStatusOptions);
    }
    setReturnsPage(1);
  };

  const toggleReplacementSelectAll = () => {
    if (replacementsStatusFilter.length === replacementStatusOptions.length) {
      setReplacementsStatusFilter([]);
    } else {
      setReplacementsStatusFilter(replacementStatusOptions);
    }
    setReplacementsPage(1);
  };

  const filteredReturns = returns.filter((r) => {
    const matchesSearch =
      returnsSearch === "" ||
      r.id.toLowerCase().includes(returnsSearch.toLowerCase()) ||
      r.customerName.toLowerCase().includes(returnsSearch.toLowerCase()) ||
      r.product.toLowerCase().includes(returnsSearch.toLowerCase()) ||
      r.orderId.toLowerCase().includes(returnsSearch.toLowerCase());
    const matchesStatus = returnsStatusFilter.length === 0 || returnsStatusFilter.includes(r.status);
    return matchesSearch && matchesStatus;
  });

  const filteredReplacements = replacements.filter((r) => {
    const searchValue = replacementsSearch.toLowerCase();
    const matchesSearch =
      searchValue === "" ||
      r.id.toLowerCase().includes(searchValue) ||
      r.customerName.toLowerCase().includes(searchValue) ||
      r.product.toLowerCase().includes(searchValue) ||
      r.orderId.toLowerCase().includes(searchValue) ||
      r.returnId.toLowerCase().includes(searchValue);
    const matchesStatus = replacementsStatusFilter.length === 0 || replacementsStatusFilter.includes(r.status);
    return matchesSearch && matchesStatus;
  });

  const sortedReturns = [...filteredReturns].sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  const sortedReplacements = [...filteredReplacements].sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  const totalReturnPages = Math.max(1, Math.ceil(sortedReturns.length / pageSize));
  const totalReplacementPages = Math.max(1, Math.ceil(sortedReplacements.length / pageSize));
  const safeReturnsPage = Math.min(returnsPage, totalReturnPages);
  const safeReplacementsPage = Math.min(replacementsPage, totalReplacementPages);
  const pagedReturns = sortedReturns.slice((safeReturnsPage - 1) * pageSize, safeReturnsPage * pageSize);
  const pagedReplacements = sortedReplacements.slice((safeReplacementsPage - 1) * pageSize, safeReplacementsPage * pageSize);

  const pendingCount = returns.filter(
    (r) => r.status === "pending_review" || r.status === "inspection"
  ).length;
  const replacementPendingCount = replacements.filter(
    (r) => r.status === "requested" || r.status === "approved"
  ).length;

  return (
    <div className="space-y-6 border rounded">
{/*       {apiError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 m-2 text-sm text-amber-700">
          {apiError}
        </div>
      )} */}

      {/* Summary Cards */}
{/*       <div className="grid grid-cols-4 gap-4">
        {[
          {
            label: "Pending Review",
            count: returns.filter((r) => r.status === "pending_review").length,
            icon: Clock,
            color: "text-yellow-600",
            bg: "bg-yellow-50",
          },
          {
            label: "Under Inspection",
            count: returns.filter((r) => r.status === "inspection").length,
            icon: Eye,
            color: "text-blue-600",
            bg: "bg-blue-50",
          },
          {
            label: "Approved",
            count: returns.filter((r) => r.status === "approved").length,
            icon: CheckCircle,
            color: "text-green-600",
            bg: "bg-green-50",
          },
          {
            label: "Replacements Active",
            count: replacements.filter(
              (r) => r.status !== "delivered" && r.status !== "rejected"
            ).length,
            icon: RefreshCw,
            color: "text-indigo-600",
            bg: "bg-indigo-50",
          },
        ].map((stat) => (
          <Card key={stat.label} className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${stat.bg}`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{stat.count}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          </Card>
        ))}
      </div> */}

      {/* Tabs: Returns | Replacements */}
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
          >
         {/* <TabsList className="bg-muted/70 p-1">
          <TabsTrigger value="returns" className="flex items-center gap-2 rounded-md border border-transparent px-3 py-2 data-[state=active]:bg-gray-100 data-[state=active]:text-black data-[state=active]:shadow-sm data-[state=active]:border-gray-100">
            <RotateCcw className="w-4 h-4" />
            Returns
            {pendingCount > 0 && (
              <Badge variant="destructive" className="ml-1 text-xs px-1.5">
                {pendingCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="replacements" className="flex items-center gap-2 rounded-md border border-transparent px-3 py-2 data-[state=active]:bg-gray-100 data-[state=active]:text-black data-[state=active]:shadow-sm data-[state=active]:border-gray-100">
            <RefreshCw className="w-4 h-4" />
            Replacements
            {replacementPendingCount > 0 && (
              <Badge variant="destructive" className="ml-1 text-xs px-1.5">
                {replacementPendingCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>*/}
        <div className="flex items-center justify-between mt-4">
          <TabsList className="bg-muted/70 p-1">
            <TabsTrigger value="returns" className="
                rounded-md
                px-4
                data-[state=active]:bg-gray-100
                data-[state=active]:text-black
                data-[state=active]:shadow-sm"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Returns

              {pendingCount > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {pendingCount}
                </Badge>
              )}
            </TabsTrigger>

           {/* <TabsTrigger value="replacements" className="
                rounded-md
                px-4
                data-[state=active]:bg-gray-100
                data-[state=active]:text-black
                data-[state=active]:shadow-sm"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Replacements

              {replacementPendingCount > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {replacementPendingCount}
                </Badge>
              )}
            </TabsTrigger>*/}
          </TabsList>
        <div className="group relative">
          <Button
            size="icon"
            variant="default"
            className="mr-4 h-9 w-9 bg-[#f7525a] text-white hover:bg-[#e5454d]"
            onClick={() => {
              setCreateType(
                activeTab === "returns" ? "return" : "replacement"
              );
              setShowCreateModal(true);
            }}
          >
            <Plus className="h-4 w-4" />
          </Button>

          <div
            className="
              absolute
              right-0
              top-full
              mt-2
              z-50
              whitespace-nowrap
              rounded-md
              bg-black
              px-2
              py-1
              text-xs
              text-white
              opacity-0
              transition-opacity
              duration-200
              pointer-events-none
              group-hover:opacity-100
            "
          >
            {activeTab === "returns"
              ? "Raise Return Request"
              : "Raise Replacement Request"}
          </div>
        </div>
        </div>

        {/* Returns Tab */}
        <TabsContent value="returns" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center m-2 gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search returns or shipments"
                value={returnsSearch}
                onChange={(e) => {
                  setReturnsSearch(e.target.value);
                  setReturnsPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="relative status-dropdown">
              <Filter className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              <button
                type="button"
                onClick={() => {
                  setShowReturnsStatusDropdown((prev) => !prev);
                  setShowReplacementsStatusDropdown(false);
                }}
                className="pl-10 pr-8 py-2 border rounded-md text-sm bg-white focus:outline-none min-w-[220px] text-left"
              >
                {returnsStatusFilter.length === 0 ? "Return Status" : `${returnsStatusFilter.length} Selected`}
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              </button>
              {showReturnsStatusDropdown && (
                <div className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg">
                  <div className="flex items-center justify-between px-3 py-2 border-b">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={returnsStatusFilter.length === returnStatusOptions.length}
                        onChange={toggleReturnSelectAll}
                      />
                      <span className="text-sm font-medium">All Status</span>
                    </label>
                    <button
                      onClick={() => {
                        setReturnsStatusFilter([]);
                        setReturnsPage(1);
                      }}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {returnStatusOptions.map((status) => (
                      <label key={status} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={returnsStatusFilter.includes(status)}
                          onChange={() => toggleReturnStatus(status)}
                        />
                        <span>{statusConfig[status]?.label || status}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Return List */}
          <div className="space-y-2">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading returns...</div>
            ) : filteredReturns.length === 0 ? (
              <div className="text-center rounded-lg border border-gray-100 py-16 flex flex-col items-center justify-center text-gray-500">
                <PackageX className="w-8 h-8 mb-3 text-gray-400" />
                <p className="text-sm font-medium">No returns found</p>
              </div>
            ) : (
              <>
                {pagedReturns.map((item) => (
                  <ReturnRow
                    key={item.id}
                    item={item}
                    onSelect={setSelectedReturn}
                    onQuickAction={(id, status) => handleReturnStatusChange(id, status)}
                    setRejectModal={setRejectModal}
                  />
                ))}
                {sortedReturns.length > pageSize && (
                  <div className="rounded-lg border bg-white">
                    <Pagination
                      currentPage={safeReturnsPage}
                      totalPages={totalReturnPages}
                      totalItems={sortedReturns.length}
                      itemsPerPage={pageSize}
                      onPageChange={(page) => setReturnsPage(page)}
                      onPreviousPage={() => setReturnsPage((page) => Math.max(1, page - 1))}
                      onNextPage={() => setReturnsPage((page) => Math.min(totalReturnPages, page + 1))}
                      showInfo={true}
                      itemName="returns"
                      className="border-0 shadow-none"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>

        {/* Replacements Tab */}
        <TabsContent value="replacements" className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search replacements or shipments"
                value={replacementsSearch}
                onChange={(e) => {
                  setReplacementsSearch(e.target.value);
                  setReplacementsPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="relative status-dropdown">
              <Filter className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              <button
                type="button"
                onClick={() => {
                  setShowReplacementsStatusDropdown((prev) => !prev);
                  setShowReturnsStatusDropdown(false);
                }}
                className="pl-10 pr-8 py-2 border rounded-md text-sm bg-white focus:outline-none min-w-[220px] text-left"
              >
                {replacementsStatusFilter.length === 0 ? "Replacement Status" : `${replacementsStatusFilter.length} Selected`}
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              </button>
              {showReplacementsStatusDropdown && (
                <div className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg">
                  <div className="flex items-center justify-between px-3 py-2 border-b">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={replacementsStatusFilter.length === replacementStatusOptions.length}
                        onChange={toggleReplacementSelectAll}
                      />
                      <span className="text-sm font-medium">All Status</span>
                    </label>
                    <button
                      onClick={() => {
                        setReplacementsStatusFilter([]);
                        setReplacementsPage(1);
                      }}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {replacementStatusOptions.map((status) => (
                      <label key={status} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={replacementsStatusFilter.includes(status)}
                          onChange={() => toggleReplacementStatus(status)}
                        />
                        <span>{replacementStatusConfig[status]?.label || status}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading replacements...</div>
            ) : (
              <>
                {pagedReplacements.map((item) => (
                  <ReplacementRow
                    key={item.id}
                    item={item}
                    onSelect={setSelectedReplacement}
                    onQuickAction={(id, status) => handleReplacementStatusChange(id, status)}
                    setRejectModal={setRejectModal}
                  />
                ))}
                {sortedReplacements.length > pageSize && (
                  <div className="rounded-lg border bg-white">
                    <Pagination
                      currentPage={safeReplacementsPage}
                      totalPages={totalReplacementPages}
                      totalItems={sortedReplacements.length}
                      itemsPerPage={pageSize}
                      onPageChange={(page) => setReplacementsPage(page)}
                      onPreviousPage={() => setReplacementsPage((page) => Math.max(1, page - 1))}
                      onNextPage={() => setReplacementsPage((page) => Math.min(totalReplacementPages, page + 1))}
                      showInfo={true}
                      itemName="replacements"
                      className="border-0 shadow-none"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {showCreateModal && (
        <CreateRequestModal
          type={createType}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
          fetchRequests(); // triggers get-all-return-requests API call
          setShowCreateModal(false);
        }}
        />
      )}

      {rejectModal.open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-4">
              Reject {rejectModal.type === "return" ? "Return" : "Replacement"}
            </h2>

            <textarea
              rows={4}
              className="w-full border rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />

            <div className="flex justify-end gap-3 mt-5">
              <Button
                variant="outline"
                onClick={() => {
                  setRejectModal({ open: false, id: "", type: "" });
                  setRejectionReason("");
                }}
              >
                Cancel
              </Button>

             <Button
  variant="destructive"
  className="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 shadow-xs transition-colors disabled:opacity-50 disabled:bg-red-400 disabled:cursor-not-allowed cursor-pointer"
  disabled={!rejectionReason.trim()}
  onClick={async () => {
    if (rejectModal.type === "return") {
      await handleReturnStatusChange(
        rejectModal.id,
        "rejected",
        rejectionReason
      );
    } else {
      await handleReplacementStatusChange(
        rejectModal.id,
        "rejected",
        rejectionReason
      );
    }

    setRejectModal({ open: false, id: "", type: "" });
    setRejectionReason("");
  }}
>
  Reject
</Button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Panels */}
      {selectedReturn && (
        <ReturnDetailPanel
          item={selectedReturn}
          onClose={() => setSelectedReturn(null)}
          onStatusChange={handleReturnStatusChange}
          setRejectModal={setRejectModal}
        />
      )}
      {selectedReplacement && (
        <ReplacementDetailPanel
          item={selectedReplacement}
          onClose={() => setSelectedReplacement(null)}
          onStatusChange={handleReplacementStatusChange}
          setRejectModal={setRejectModal}
        />
      )}
    </div>
  );
}
